'use client'

import { motion, useInView, type Variants } from 'framer-motion'
import { useRef } from 'react'
import { cn } from '@/utils/cn'

// 왼쪽에서 슬라이드 인 애니메이션
interface SlideInLeftProps {
  text: string
  className?: string
  delay?: number
  duration?: number
}

export function SlideInLeft({ text, className, delay = 0, duration = 0.5 }: SlideInLeftProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, {
    once: true,
    amount: 0.3,
    margin: '0px 0px -50px 0px',
  })

  return (
    <motion.span
      ref={ref}
      className={cn('inline-block', className)}
      initial={{ opacity: 0, x: -100 }}
      animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -100 }}
      transition={{
        duration,
        delay,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
    >
      {text}
    </motion.span>
  )
}

// 오른쪽에서 슬라이드 인 애니메이션
interface SlideInRightProps {
  text: string
  className?: string
  delay?: number
  duration?: number
}

export function SlideInRight({ text, className, delay = 0, duration = 0.5 }: SlideInRightProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, {
    once: true,
    amount: 0.3,
    margin: '0px 0px -50px 0px',
  })

  return (
    <motion.span
      ref={ref}
      className={cn('inline-block', className)}
      initial={{ opacity: 0, x: 100 }}
      animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 100 }}
      transition={{
        duration,
        delay,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
    >
      {text}
    </motion.span>
  )
}

interface WaveTextProps {
  text: string
  className?: string
  delay?: number
  duration?: number
}

export function WaveText({ text, className, delay = 0, duration = 0.5 }: WaveTextProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, {
    once: true,
    amount: 0.3,
    margin: '0px 0px -50px 0px',
  })

  const container: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: delay,
        staggerChildren: 0.05,
      },
    },
  }

  const child: Variants = {
    hidden: {
      opacity: 0,
      y: 50,
      rotateX: -90,
    },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: {
        type: 'spring',
        damping: 12,
        stiffness: 200,
        duration,
      },
    },
  }

  return (
    <motion.span
      ref={ref}
      className={cn('inline-block', className)}
      variants={container}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      style={{ perspective: 1000 }}
    >
      {text.split('').map((char, index) => (
        <motion.span
          key={`char-${index}-${char}`}
          className="inline-block origin-bottom"
          variants={child}
          style={{ transformStyle: 'preserve-3d' }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
    </motion.span>
  )
}

// 빠른 페이드 인 애니메이션 (설명 텍스트용)
interface FastFadeInProps {
  text: string
  className?: string
  delay?: number
}

export function FastFadeIn({ text, className, delay = 0 }: FastFadeInProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, {
    once: true,
    amount: 0.3,
    margin: '0px 0px -50px 0px',
  })

  return (
    <motion.span
      ref={ref}
      className={cn('inline-block', className)}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{
        duration: 0.3,
        delay,
        ease: 'easeOut',
      }}
    >
      {text}
    </motion.span>
  )
}

interface ScrambleTextProps {
  text: string
  className?: string
  delay?: number
}

export function ScrambleText({ text, className, delay = 0 }: ScrambleTextProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()'

  const scrambleVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delay,
        duration: 0.1,
      },
    },
  }

  return (
    <motion.span
      ref={ref}
      className={cn('inline-block', className)}
      variants={scrambleVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      {isInView && (
        <motion.span
          animate={{
            opacity: 1,
          }}
          transition={{ duration: 0.5 }}
        >
          {text.split('').map((char, index) => (
            <motion.span
              key={`scramble-${index}-${char}`}
              className="inline-block"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: 0.5,
                delay: delay + index * 0.05,
              }}
              onAnimationStart={() => {
                const element = document.getElementById(`scramble-${index}`)
                if (element) {
                  let iteration = 0
                  const interval = setInterval(() => {
                    if (iteration < 10) {
                      element.textContent = characters[Math.floor(Math.random() * characters.length)]
                      iteration++
                    } else {
                      element.textContent = char
                      clearInterval(interval)
                    }
                  }, 50)
                }
              }}
            >
              <span id={`scramble-${index}`}>{char}</span>
            </motion.span>
          ))}
        </motion.span>
      )}
    </motion.span>
  )
}

interface RotateInTextProps {
  text: string
  className?: string
  delay?: number
}

export function RotateInText({ text, className, delay = 0 }: RotateInTextProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, {
    once: true,
    amount: 0.3,
    margin: '0px 0px -50px 0px',
  })

  const words = text.split(' ')

  const container: Variants = {
    hidden: { opacity: 1 }, // 초기 opacity를 1로 설정
    visible: {
      opacity: 1,
      transition: {
        delayChildren: delay,
        staggerChildren: 0.1,
      },
    },
  }

  const child: Variants = {
    hidden: {
      opacity: 0,
      rotateY: 180,
      scale: 0.8,
    },
    visible: {
      opacity: 1,
      rotateY: 0,
      scale: 1,
      transition: {
        type: 'spring',
        damping: 15,
        stiffness: 100,
      },
    },
  }

  return (
    <motion.span
      ref={ref}
      className={cn('inline-flex gap-2', className)}
      variants={container}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      style={{ perspective: 1000 }}
    >
      {words.map((word, index) => (
        <motion.span
          key={`word-${index}-${word}`}
          className="inline-block"
          variants={child}
          style={{ transformStyle: 'preserve-3d', opacity: 0 }} // 초기 opacity 설정
        >
          {word}
        </motion.span>
      ))}
    </motion.span>
  )
}

interface PopInTextProps {
  text: string
  className?: string
  delay?: number
}

export function PopInText({ text, className, delay = 0 }: PopInTextProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, {
    once: true,
    amount: 0.3,
    margin: '0px 0px -50px 0px',
  })

  const chars = text.split('')

  return (
    <motion.span ref={ref} className={cn('inline-block', className)}>
      {isInView &&
        chars.map((char, index) => (
          <motion.span
            key={`pop-${index}-${char}`}
            className="inline-block"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 20,
              delay: delay + index * 0.03,
            }}
          >
            {char === ' ' ? '\u00A0' : char}
          </motion.span>
        ))}
    </motion.span>
  )
}

interface AnimatedGradientTextProps {
  children: string
  className?: string
  colors?: string[]
  animationSpeed?: number
  delay?: number
}

export function AnimatedGradientText({
  children,
  className,
  colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'],
  animationSpeed = 5,
  delay = 0,
}: AnimatedGradientTextProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, {
    once: true,
    amount: 0.3,
    margin: '0px 0px -50px 0px',
  })

  const gradientStyle = {
    backgroundImage: `linear-gradient(90deg, ${colors.join(', ')}, ${colors[0]})`,
    backgroundSize: '200% auto',
  }

  return (
    <motion.span
      ref={ref}
      className={cn('inline-block bg-clip-text text-transparent', className)}
      style={gradientStyle}
      initial={{ opacity: 0, y: 0 }} // y를 0으로 변경하여 초기 위치 유지
      animate={
        isInView
          ? {
              opacity: 1,
              y: 0,
              backgroundPosition: ['0% center', '200% center'],
            }
          : { opacity: 0, y: 0 } // hidden 상태에서도 y를 0으로 유지
      }
      transition={{
        opacity: { duration: 0.5, delay },
        y: { duration: 0.5, delay },
        backgroundPosition: {
          duration: animationSpeed,
          repeat: Infinity,
          repeatType: 'loop',
          ease: 'linear',
          delay,
        },
      }}
    >
      {children}
    </motion.span>
  )
}

interface StaggeredRevealProps {
  children: React.ReactNode
  className?: string
  delay?: number
}

export function StaggeredReveal({ children, className, delay = 0 }: StaggeredRevealProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{
        delayChildren: delay,
        staggerChildren: 0.15,
      }}
    >
      {children}
    </motion.div>
  )
}

interface RevealTextProps {
  children: string
  className?: string
  delay?: number
}

export function RevealText({ children, className, delay = 0 }: RevealTextProps) {
  return (
    <motion.div
      className={cn('overflow-hidden', className)}
      initial={{ height: 0 }}
      animate={{ height: 'auto' }}
      transition={{
        duration: 0.5,
        delay,
        ease: 'easeOut',
      }}
    >
      <motion.span
        className="inline-block"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        transition={{
          duration: 0.5,
          delay: delay + 0.1,
          ease: [0.25, 0.46, 0.45, 0.94],
        }}
      >
        {children}
      </motion.span>
    </motion.div>
  )
}
